import * as types from './types';

export const InteractionSxDefaults: Record<types.Interaction, types.InteractionStyle<never>> = {
  [types.Interaction.Focus]: {
    backgroundColor: 'action.hover',
    borderColor: 'secondary.main',
    borderWidth: 2,
    useOutline: true,
  },
  [types.Interaction.FocusVisible]: {
    backgroundColor: 'action.hover',
    borderColor: 'secondary.main',
    borderWidth: 2,
    useOutline: true,
  },
  [types.Interaction.FocusWithin]: {
    backgroundColor: 'action.hover',
    borderColor: 'secondary.main',
    borderWidth: 2,
    useOutline: true,
  },
  [types.Interaction.Hover]: {
    backgroundColor: 'action.hover',
    borderColor: 'action.active',
    borderWidth: 1,
    useOutline: true,
  },
};

/**
 * Maps each {@link CustomInteractionStyle} property key to the {@link InteractionIndicator} it
 * belongs to. Used to determine which indicator a given custom style property is associated with
 * when filtering styles by indicator.
 */
const InteractionStyleIndicatorMap: Record<
  types.InteractionStyleProperty,
  types.InteractionIndicator
> = {
  backgroundColor: types.InteractionIndicator.Background,
  borderColor: types.InteractionIndicator.Border,
  borderWidth: types.InteractionIndicator.Border,
  useOutline: types.InteractionIndicator.Border,
};

/**
 * Returns true if the given {@link types.InteractionStyle} property key is associated with the
 * provided {@link types.InteractionIndicator} or array of indicators.
 *
 * @param {types.InteractionStyleProperty} key The custom style property to check.
 * @param {types.InteractionIndicators} indicator The indicator or indicators to check against.
 */
export const isInteractionStylePropertyAssociatedWithIndicator = (
  key: types.InteractionStyleProperty,
  indicator: types.InteractionIndicators,
): boolean => {
  if (Array.isArray(indicator)) {
    return indicator.includes(InteractionStyleIndicatorMap[key]);
  }
  return indicator === InteractionStyleIndicatorMap[key];
};

const DefaultInteractionIndicators: Record<types.Interaction, types.InteractionIndicator[]> = {
  [types.Interaction.Focus]: [types.InteractionIndicator.Border],
  [types.Interaction.FocusVisible]: [types.InteractionIndicator.Border],
  [types.Interaction.FocusWithin]: [types.InteractionIndicator.Border],
  [types.Interaction.Hover]: [types.InteractionIndicator.Background],
};

export const getDefaultInteractionIndicators = (
  interaction: types.Interaction,
): types.InteractionIndicator[] => DefaultInteractionIndicators[interaction];

type GetDefaultInteractionStyleOptions = {
  readonly property?: types.InteractionStyleProperty;
};

type ResolvedDefaultInteractionStyle<O extends GetDefaultInteractionStyleOptions> = O extends {
  property: infer P;
}
  ? P extends types.InteractionStyleProperty
    ? types.InteractionStyle<never>[P]
    : never
  : types.InteractionStyle<never>;

/**
 * Returns the default {@link CustomInteractionStyle} values for a given {@link Interaction}. If
 * `options.property` is specified, only that property's default value is returned.
 *
 * @param {types.Interaction} interaction
 *   The interaction to retrieve and resolve the defaulted style for.
 * @param {O} [options] Optionally narrow the result to a single style property.
 */
export const getDefaultInteractionStyle = <O extends GetDefaultInteractionStyleOptions>(
  interaction: types.Interaction,
  options?: O,
): ResolvedDefaultInteractionStyle<O> => {
  const style = InteractionSxDefaults[interaction];
  if (options?.property) {
    return style[options.property] as ResolvedDefaultInteractionStyle<O>;
  }
  let pluckedStyle: Partial<types.InteractionStyle<never>> = {};
  for (const key of types.InteractionStyleProperties) {
    if (
      isInteractionStylePropertyAssociatedWithIndicator(
        key,
        getDefaultInteractionIndicators(interaction),
      )
    ) {
      pluckedStyle = { ...pluckedStyle, [key]: style[key] };
    }
  }
  return pluckedStyle as ResolvedDefaultInteractionStyle<O>;
};
